// 用語アシスト UI (spec §4.5)。
// 三つのフローを 1 コンポーネントに統合:
// - フロー A: PDF アップ → AI 抽出 → 候補レビュー → 選択して保存
// - フロー B: 用語名入力 → AI で解説生成 → 編集 → 保存
// - フロー C: 用語名 + 解説を直接入力 → 保存
//
// CLAUDE.md 必須:
// - AI 生成コンテンツには「AI 生成」ラベル必須 → source バッジで表示
// - AI からの発信はホスト承認 UI 経由 → 「保存」または「投下」ボタンを押すまで何も発火しない

import {
  useCallback,
  useEffect,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactElement,
} from 'react';
import { t, type Locale, type MessageKey } from '@solo-webinar-copilot/i18n';
import type { Term, TermSource } from '@solo-webinar-copilot/shared';
import {
  createTerm,
  deleteTerm,
  describeTermWithAi,
  extractTermsFromPdf,
  listTerms,
  postTerm,
  type ExtractedTermCandidate,
} from '../lib/api-client.ts';
import type { AppContext } from '../lib/zoom-sdk.ts';

const PDF_MAX_BYTES = 5 * 1024 * 1024;

const SOURCE_LABEL_KEY: Record<TermSource, MessageKey> = {
  manual: 'in_client.host.terms.source.manual',
  ai_generated: 'in_client.host.terms.source.ai_generated',
  from_slide: 'in_client.host.terms.source.from_slide',
};

export interface TermsProps {
  ctx: AppContext;
  locale: Locale;
}

export function Terms({ ctx, locale }: TermsProps): ReactElement {
  const [items, setItems] = useState<Term[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [postedId, setPostedId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const r = await listTerms(ctx);
    if (r.ok) {
      setItems(r.items);
      setError(null);
    } else {
      setError(r.error);
    }
    setLoading(false);
  }, [ctx]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handlePost = async (id: string) => {
    setError(null);
    const r = await postTerm(ctx, id);
    if (r.ok) setPostedId(id);
    else setError(r.error);
  };

  const handleDelete = async (id: string) => {
    if (
      typeof window !== 'undefined' &&
      !window.confirm(t('in_client.host.terms.confirm_delete', locale))
    ) {
      return;
    }
    setError(null);
    const r = await deleteTerm(ctx, id);
    if (r.ok) setItems((prev) => prev.filter((x) => x.id !== id));
    else setError(r.error);
  };

  const handleSaveCandidates = async (selected: ExtractedTermCandidate[]) => {
    for (const c of selected) {
      const r = await createTerm(ctx, {
        term: c.term,
        description: c.description,
        source: 'from_slide',
      });
      if (r.ok) setItems((prev) => [...prev, r.item]);
    }
  };

  return (
    <section data-testid="terms">
      <h2>{t('in_client.host.terms.title', locale)}</h2>
      {error ? (
        <p role="alert" data-testid="terms-error">
          {t('in_client.error.network', locale)}: {error}
        </p>
      ) : null}
      {loading ? (
        <p data-testid="terms-loading">…</p>
      ) : items.length === 0 ? (
        <p data-testid="terms-empty">{t('in_client.host.terms.empty', locale)}</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {items.map((it) => (
            <li
              key={it.id}
              data-testid={`term-item-${it.id}`}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                padding: 8,
                marginBottom: 6,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <strong>{it.term}</strong>
                {it.source ? (
                  <small data-testid={`term-source-${it.id}`} style={{ opacity: 0.6 }}>
                    [{t(SOURCE_LABEL_KEY[it.source], locale)}]
                  </small>
                ) : null}
              </div>
              <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0' }}>{it.description}</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => void handlePost(it.id)}
                  data-testid={`term-post-${it.id}`}
                >
                  {t('in_client.host.terms.post', locale)}
                </button>
                <button
                  type="button"
                  onClick={() => void handleDelete(it.id)}
                  data-testid={`term-delete-${it.id}`}
                >
                  {t('in_client.host.terms.delete', locale)}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {postedId ? (
        <p data-testid="term-posted">{t('in_client.host.terms.posted', locale)}</p>
      ) : null}

      {showForm ? (
        <TermForm
          ctx={ctx}
          locale={locale}
          onCancel={() => setShowForm(false)}
          onSaved={(item) => {
            setItems((prev) => [...prev, item]);
            setShowForm(false);
          }}
          onError={(msg) => setError(msg)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          data-testid="term-add-button"
        >
          {t('in_client.host.terms.add', locale)}
        </button>
      )}

      <PdfExtractor
        ctx={ctx}
        locale={locale}
        onSave={handleSaveCandidates}
        onError={(m) => setError(m)}
      />
    </section>
  );
}

interface TermFormProps {
  ctx: AppContext;
  locale: Locale;
  onCancel: () => void;
  onSaved: (item: Term) => void;
  onError: (message: string) => void;
}

function TermForm({
  ctx,
  locale,
  onCancel,
  onSaved,
  onError,
}: TermFormProps): ReactElement {
  const [term, setTerm] = useState('');
  const [contextInput, setContextInput] = useState('');
  const [description, setDescription] = useState('');
  // 解説を AI で生成したら true。保存時に source = 'ai_generated' で保存する。
  // 編集後でも履歴は残らないので、ホストが大きく書き換えた場合 'manual' に変える操作は今は無し。
  const [aiUsed, setAiUsed] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleAi = async () => {
    if (!term.trim()) return;
    setGenerating(true);
    const r = await describeTermWithAi(
      ctx,
      term.trim(),
      contextInput.trim() || undefined,
    );
    setGenerating(false);
    if (r.ok) {
      setDescription(r.description);
      setAiUsed(true);
    } else {
      onError(r.error);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!term.trim() || !description.trim()) return;
    setSubmitting(true);
    const source: TermSource = aiUsed ? 'ai_generated' : 'manual';
    const r = await createTerm(ctx, {
      term: term.trim(),
      description: description.trim(),
      source,
    });
    setSubmitting(false);
    if (r.ok) onSaved(r.item);
    else onError(r.error);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="term-form">
      <div>
        <label>
          {t('in_client.host.terms.term', locale)}
          <input
            type="text"
            value={term}
            maxLength={120}
            onChange={(e) => setTerm(e.currentTarget.value)}
            data-testid="term-form-name"
            required
          />
        </label>
      </div>
      <div>
        <label>
          {t('in_client.host.terms.context', locale)}
          <input
            type="text"
            value={contextInput}
            maxLength={1000}
            onChange={(e) => setContextInput(e.currentTarget.value)}
            data-testid="term-form-context"
          />
        </label>
      </div>
      <div>
        <button
          type="button"
          onClick={() => void handleAi()}
          disabled={generating || !term.trim()}
          data-testid="term-form-ai"
        >
          {generating
            ? t('in_client.host.terms.ai_generating', locale)
            : t('in_client.host.terms.ai_describe', locale)}
        </button>
      </div>
      <div>
        <label>
          {t('in_client.host.terms.description', locale)}
          {aiUsed ? (
            <small
              data-testid="term-form-ai-badge"
              style={{ marginLeft: 6, opacity: 0.7 }}
            >
              [{t('in_client.ai.generated_label', locale)}]
            </small>
          ) : null}
          <textarea
            value={description}
            maxLength={4000}
            onChange={(e) => {
              setDescription(e.currentTarget.value);
              // ホストが手で書き換えても AI 生成バッジは残す方針 (出自情報として価値があるため)。
              // 完全置換時は別ハンドリングを検討。MVP では出自を尊重。
            }}
            data-testid="term-form-description"
            required
            rows={5}
          />
        </label>
      </div>
      <div>
        <button type="submit" disabled={submitting} data-testid="term-form-save">
          {t('in_client.host.terms.save', locale)}
        </button>
        <button type="button" onClick={onCancel} data-testid="term-form-cancel">
          {t('in_client.host.terms.cancel', locale)}
        </button>
      </div>
    </form>
  );
}

interface PdfExtractorProps {
  ctx: AppContext;
  locale: Locale;
  onSave: (selected: ExtractedTermCandidate[]) => Promise<void>;
  onError: (message: string) => void;
}

function PdfExtractor({
  ctx,
  locale,
  onSave,
  onError,
}: PdfExtractorProps): ReactElement {
  const [file, setFile] = useState<File | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [candidates, setCandidates] = useState<ExtractedTermCandidate[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.currentTarget.files?.[0] ?? null;
    if (f && f.size > PDF_MAX_BYTES) {
      onError(t('in_client.host.terms.pdf_too_large', locale));
      setFile(null);
      return;
    }
    setFile(f);
    setCandidates([]);
    setSelected(new Set());
  };

  const handleExtract = async () => {
    if (!file) return;
    setExtracting(true);
    const r = await extractTermsFromPdf(ctx, file);
    setExtracting(false);
    if (r.ok) {
      setCandidates(r.candidates);
      // デフォルト全選択
      setSelected(new Set(r.candidates.map((_, i) => i)));
    } else {
      onError(r.error);
    }
  };

  const toggle = (i: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const handleSave = async () => {
    const picked = candidates.filter((_, i) => selected.has(i));
    await onSave(picked);
    setCandidates([]);
    setSelected(new Set());
    setFile(null);
  };

  return (
    <div data-testid="pdf-extractor" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
      <h3>{t('in_client.host.terms.pdf_section', locale)}</h3>
      <input
        type="file"
        accept="application/pdf"
        onChange={handleFile}
        data-testid="pdf-file-input"
      />
      <button
        type="button"
        onClick={() => void handleExtract()}
        disabled={!file || extracting}
        data-testid="pdf-extract-button"
      >
        {extracting
          ? t('in_client.host.terms.pdf_extracting', locale)
          : t('in_client.host.terms.pdf_extract', locale)}
      </button>
      {candidates.length > 0 ? (
        <div data-testid="pdf-candidates">
          <h4>
            {t('in_client.host.terms.pdf_candidates', locale)}{' '}
            <small style={{ opacity: 0.7 }}>
              [{t('in_client.ai.generated_label', locale)}]
            </small>
          </h4>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {candidates.map((c, i) => (
              <li
                key={i}
                data-testid={`pdf-candidate-${i}`}
                style={{ marginBottom: 4 }}
              >
                <label>
                  <input
                    type="checkbox"
                    checked={selected.has(i)}
                    onChange={() => toggle(i)}
                    data-testid={`pdf-candidate-check-${i}`}
                  />{' '}
                  <strong>{c.term}</strong>: {c.description}
                </label>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={selected.size === 0}
            data-testid="pdf-save-selected"
          >
            {t('in_client.host.terms.pdf_save_selected', locale)}
          </button>
        </div>
      ) : null}
    </div>
  );
}
